import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import clsx from 'clsx'
import PropTypes from 'prop-types'

import Icon from '../../../../../UI/Icon'
import BtnTooltip from '../../../../../UI/BtnTooltip'
import http from '../../../../../shared/http'
import { TApi } from '../../../../../shared/const'

import styles from '../style.module.scss'
import { formatVienna } from '../../../../../shared/datetime'

const ACCEPT = 'application/pdf'

/**
 * Eine Unterlage als Kachel.
 *
 * Die Kachel selbst fuehrt die naechstliegende Handlung aus: liegt nichts vor,
 * ist das Hochladen; liegt etwas vor, das Oeffnen. Gibt es daneben noch eine
 * zweite Moeglichkeit (Ersetzen), steht sie im Drei-Punkte-Menue oben rechts —
 * so bleibt die Kachel eindeutig und die selteneren Wege sind trotzdem da.
 *
 * `onUpload` fehlt bei Unterlagen, die die Verwaltung nicht selbst hinterlegen
 * kann: beim Firmenbuchauszug, den die Aktivierung erzeugt.
 */
function DocumentCard({ icon, title, hint, document, onUpload, uploading }) {
	const { t } = useTranslation()
	const inputRef = useRef(null)
	const [downloading, setDownloading] = useState(false)

	const open = () => {
		if (!document) return
		setDownloading(true)
		http.get(TApi.GET_FILES_DOCUMENT(document.name), { responseType: 'blob' })
			.then(({ data }) => window.open(URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))))
			.catch(() => {}) // Meldung kommt aus shared/http
			.finally(() => setDownloading(false))
	}

	const pickFile = () => inputRef.current?.click()

	const primary = document ? open : onUpload ? pickFile : null
	const primaryLabel = document ? t('Open document') : onUpload ? t('Upload') : t('Not available')

	// Nur was ueber die Hauptaktion hinausgeht, kommt ins Menue.
	const extraActions = []
	if (document && onUpload) extraActions.push({ label: t('Replace'), onClick: pickFile })

	const busy = downloading || uploading

	return (
		<div
			className={clsx(styles.docCard, primary && styles.clickableCard, busy && styles.docCardBusy)}
			role={primary ? 'button' : undefined}
			tabIndex={primary ? 0 : undefined}
			title={primary ? primaryLabel : undefined}
			onClick={event => {
				if (!primary || event.target.closest('button, a, input, label')) return
				primary()
			}}
			onKeyDown={event => {
				if (!primary) return
				if (event.key !== 'Enter' && event.key !== ' ') return
				if (event.target !== event.currentTarget) return
				event.preventDefault()
				primary()
			}}
		>
			{!!extraActions.length && (
				<div className={styles.docCardMenu}>
					<BtnTooltip items={extraActions} />
				</div>
			)}

			<span className={styles.docIconWrap}>
				<Icon name={icon} noFill className={styles.docIcon} size={{ w: 24, h: 24 }} color="#008b9c" />
			</span>

			<h4 className={styles.docTitle}>{title}</h4>
			<span className={document ? styles.docBadgeOn : styles.docBadgeOff}>
				{document ? t('Available') : t('Missing')}
			</span>

			{document ? (
				<span className={styles.docMeta}>
					{document.originalName}
					{document.createdAt && ` · ${formatVienna(document.createdAt, 'dd.MM.yyyy')}`}
				</span>
			) : (
				<span className={clsx(styles.docMeta, styles.docMetaEmpty)}>{hint}</span>
			)}

			<span className={styles.docAction}>{busy ? t('Please wait') : primaryLabel}</span>

			{onUpload && (
				<input
					ref={inputRef}
					type="file"
					accept={ACCEPT}
					className={styles.hiddenFileInput}
					onChange={evt => {
						const file = evt.target.files?.[0]
						evt.target.value = ''
						if (file) onUpload(file)
					}}
				/>
			)}
		</div>
	)
}

DocumentCard.propTypes = {
	icon: PropTypes.string.isRequired,
	title: PropTypes.string.isRequired,
	hint: PropTypes.string.isRequired,
	document: PropTypes.object,
	onUpload: PropTypes.func,
	uploading: PropTypes.bool
}

/**
 * Reiter „Dokumente": Businessplan und Abschlussbericht kann die Verwaltung
 * hinterlegen, den Firmenbuchauszug nur herunterladen — der entsteht bei der
 * Aktivierung. Der Abschlussbericht bleibt bis zur Aktivierung ohne Handlung:
 * vorher gibt es kein Geschaeftsjahr, ueber das berichtet werden koennte.
 *
 * Mit `readOnly` (Sicht der Lehrkraft) faellt das Hochladen ganz weg; geoeffnet
 * wird weiterhin ueber `GET files/document/:name`, und wer welche Datei sehen
 * darf, entscheidet dort das Backend (services/documents).
 */
export default function DocumentsTab(props) {
	const { t } = useTranslation()
	const { companyId, companyInfo, readOnly, businessPlan, companyCertificate, finalReport, onDocumentsChanged } = props
	const [uploading, setUploading] = useState(null)

	const upload = (url, file, key, successMessage) => {
		setUploading(key)
		const form = new FormData()
		form.append('document', file)

		http.post(url, form)
			.then(({ data }) => {
				onDocumentsChanged(key, data)
				toast.success(successMessage)
			})
			.catch(() => {}) // Meldung kommt aus shared/http
			.finally(() => setUploading(null))
	}

	return (
		<div className={styles.docGrid}>
			<DocumentCard
				icon="business"
				title={t('Business plan')}
				hint={t('No business plan yet')}
				document={businessPlan}
				uploading={uploading === 'businessPlan'}
				onUpload={
					readOnly
						? undefined
						: file =>
								upload(
									TApi.ADMIN_UPLOAD_COMPANY_BUSINESS_PLAN(companyId),
									file,
									'businessPlan',
									t('Business Plan uploaded successfully')
								)
				}
			/>

			<DocumentCard
				icon="document"
				title={t('Company certificate')}
				hint={
					companyInfo?.isActive
						? t('No company certificate yet')
						: t('Company certificate is created on activation')
				}
				document={companyCertificate}
			/>

			<DocumentCard
				icon="report"
				title={t('Final report')}
				hint={
					companyInfo?.isActive
						? t('No final report yet')
						: t('Final report becomes relevant after activation')
				}
				document={finalReport}
				uploading={uploading === 'finalReport'}
				onUpload={
					companyInfo?.isActive && !readOnly
						? file =>
								upload(
									TApi.ADMIN_UPLOAD_COMPANY_FINAL_REPORT(companyId),
									file,
									'finalReport',
									t('Final report uploaded successfully')
								)
						: undefined
				}
			/>
		</div>
	)
}

DocumentsTab.propTypes = {
	companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	companyInfo: PropTypes.object,
	readOnly: PropTypes.bool,
	businessPlan: PropTypes.object,
	companyCertificate: PropTypes.object,
	finalReport: PropTypes.object,
	onDocumentsChanged: PropTypes.func
}
